import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import EmptyState from "@/components/base/EmptyState";
import ShareButtons from "@/components/feature/ShareButtons";
import FavoriteButton from "@/components/feature/FavoriteButton";
import VideoModal from "@/components/feature/VideoModal";
import { useFavorites } from "@/hooks/useFavorites";
import { getClipById, getStoryById, clipList, formatDuration, formatViews } from "@/utils/catalog";

export default function ClipDetailPage() {
  const { id } = useParams();
  const clip = getClipById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [playing, setPlaying] = useState(false);

  if (!clip) {
    return (
      <PageShell active="clip" crumbs={[{ label: "首页", to: "/" }, { label: "精彩片段", to: "/category/clip" }, { label: "未找到" }]}>
        <EmptyState
          title="没有找到这段片段"
          description="片段可能已被移除，返回片段列表看看其他精彩内容。"
          actionText="返回片段列表"
          onAction={() => window.REACT_APP_NAVIGATE("/category/clip")}
        />
      </PageShell>
    );
  }

  const story = getStoryById(clip.storyId);
  const related = clipList.filter((item) => item.id !== clip.id && item.type === clip.type).slice(0, 3);
  const favorited = isFavorite(clip.id);

  return (
    <PageShell
      active="clip"
      crumbs={[
        { label: "首页", to: "/" },
        { label: "精彩片段", to: "/category/clip" },
        { label: clip.title },
      ]}
    >
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <div
            className="group relative w-full cursor-pointer overflow-hidden rounded-lg bg-foreground-950 aspect-video"
            onClick={() => setPlaying(true)}
          >
            <img
              src={clip.thumbnail}
              alt={`${clip.title}缩略图`}
              title={`${clip.title} 精彩片段`}
              className="h-full w-full object-cover object-top opacity-80 transition group-hover:opacity-70"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition group-hover:scale-110">
                <i className="ri-play-fill text-3xl"></i>
              </span>
            </div>
            <span className="absolute bottom-3 right-3 rounded bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50">
              {formatDuration(clip.duration)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-900 md:text-2xl">{clip.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-accent-800">{clip.type}</span>
            <span className="flex items-center gap-1">
              <i className="ri-play-circle-line"></i>
              {formatViews(clip.views)} 次播放
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {formatDuration(clip.duration)}
            </span>
          </div>

          <div className="mt-5 rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900">片段说明</h2>
            <p className="mt-3 text-sm leading-loose text-foreground-600">{clip.description}</p>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
              <ShareButtons title={clip.title} />
              <FavoriteButton active={favorited} onToggle={() => toggleFavorite(clip.id)} />
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          {story && (
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-900">关联剧情</h2>
              <Link to={`/detail/story/${story.id}`} className="group mt-3 block overflow-hidden rounded-lg bg-background-100">
                <div className="aspect-[16/9] w-full overflow-hidden">
                  <img
                    src={story.cover}
                    alt={`${story.title}剧照`}
                    title={`${story.title} 剧情介绍`}
                    className="h-full w-full object-cover object-top transition group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm text-foreground-900 transition group-hover:text-primary-600">{story.title}</p>
                  <p className="mt-1 text-xs text-foreground-500">
                    {story.season} · 第 {story.episode} 集
                  </p>
                </div>
              </Link>
            </div>
          )}

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-900">相关片段</h2>
            <div className="mt-3 space-y-3">
              {related.map((item) => (
                <Link key={item.id} to={`/detail/clip/${item.id}`} className="group flex items-center gap-3">
                  <div className="relative h-14 w-24 flex-shrink-0 overflow-hidden rounded-md bg-background-100">
                    <img
                      src={item.thumbnail}
                      alt={`${item.title}缩略图`}
                      title={`${item.title} 精彩片段`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm text-foreground-800 transition group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-400">{formatDuration(item.duration)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/category/clip"
            className="flex items-center justify-center gap-1 rounded-lg border border-background-200/70 bg-background-100 py-3 text-sm text-foreground-600 transition hover:text-primary-600"
          >
            <i className="ri-arrow-left-line"></i>返回片段列表
          </Link>
        </aside>
      </div>

      <VideoModal open={playing} title={clip.title} thumbnail={clip.thumbnail} onClose={() => setPlaying(false)} />
    </PageShell>
  );
}